/**
 * Gestão de Pedidos — finalização do atendimento e apoio do financeiro.
 *
 * O atendimento finaliza o caso e encaminha ao financeiro somente o que
 * não resolveu, com motivo obrigatório. A resposta financeira encerra esse
 * encaminhamento; os fluxos físicos, fiscais e do ML são independentes.
 * Casos legados que pediram apoio antes da finalização preservam a revisão
 * do supervisor. `rmas:resolve` permite reabrir o atendimento com motivo.
 *
 * O painel `CaseReviewPanel` mora na Pós-venda, dentro do caso expandido e
 * ACIMA do `RmaDetail` embutido (o cabeçalho recolhido continua limpo). O
 * `FinanceSupportCasesPanel` repete o apoio em aberto na aba Financeiro, com
 * a mesma ação. Wire (`review` + `finance_support`, snake_case) igual ao do
 * `/rmas/:id`; sem `review` no wire (backend antigo) nada aparece. Depois de
 * cada ação, `onChanged()` recarrega o pedido (linha, tags e histórico).
 *
 * Linguagem visual do módulo: plana, cantos QUADRADOS, negrito só na ação
 * primária, sem vermelho (nada aqui é urgência — o SLA é das tags).
 */
import { useEffect, useState } from 'react';
import { OccurrenceTitle } from '../../components/pedidos/pedidosShared';
import { fmtDate, Panel } from '../../components/rma/rmaShared';
import { useToast } from '../../components/ui/Toast';
import {
    CASE_REVIEW_DECISION_LABELS,
    CASE_REVIEW_NOTE_MAX,
    REQUIRED_NOTE_MIN,
    TONE_CLASSES,
} from '../../constants/pedidoTags';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { PRIMARY_BUTTON_CLASSES, SECONDARY_BUTTON_CLASSES } from './listParts';

/** Campo de texto plano do módulo (mesmo traço da nota do histórico). */
export const NOTE_FIELD_CLASSES =
    'w-full border border-mono-300 bg-white p-2 text-[13px] text-black outline-none transition-colors focus:border-black disabled:opacity-50 dark:border-mono-700 dark:bg-mono-950 dark:text-white dark:focus:border-white';

const SECTION_LABEL = 'text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600';
const META_TEXT = 'text-[11px] text-mono-400 dark:text-mono-500';
const ERROR_TEXT = 'text-[11px] text-danger-600 dark:text-danger-400';
const LINK_BUTTON_CLASS =
    'text-[11px] text-mono-500 underline underline-offset-2 transition-colors hover:text-black disabled:opacity-50 dark:text-mono-400 dark:hover:text-white';

/** Observação obrigatória: ≥ 3 caracteres depois do trim (mesma regra do backend). */
export function HasRequiredNote(text) {
    return String(text || '').trim().length >= REQUIRED_NOTE_MIN;
}

/** "por Fulano em 29/09/2026, 10:12" — partes ausentes somem; nada → ''. */
export function ActorLine(name, at) {
    const who = name ? `por ${name}` : '';
    const when = at ? `em ${fmtDate(at)}` : '';
    return [who, when].filter(Boolean).join(' ');
}

/** Chip do estado do atendimento (tom de `CaseReviewState`, nunca red). */
export const CaseReviewBadge = ({ state }) => {
    if (!state) {
        return null;
    }
    const classes = TONE_CLASSES[state.tone] || TONE_CLASSES.neutral;
    return (
        <span
            className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 text-[11px] font-semibold ${classes.chip} ${classes.ring}`}
        >
            {state.label}
        </span>
    );
};

/** Registro do atendimento: rótulo, quem/quando e texto completo. */
const ReviewEntry = ({ label, name, at, text, tone = 'neutral' }) => {
    const classes = TONE_CLASSES[tone] || TONE_CLASSES.neutral;
    const actor = ActorLine(name, at);
    return (
        <div className={`border-l-2 pl-3 ${classes.border}`}>
            <p className="text-[12px] text-mono-700 dark:text-mono-200">
                {label}
                {actor ? <span className="ml-1.5 text-mono-400 dark:text-mono-500">{actor}</span> : null}
            </p>
            {text ? (
                <p className="whitespace-pre-wrap break-words text-[13px] text-mono-800 dark:text-mono-100">{text}</p>
            ) : null}
        </div>
    );
};

/**
 * Apoio do financeiro de UM caso: o motivo do encaminhamento e a resposta (texto
 * completo, sempre que existirem) e, com o apoio ABERTO e
 * `rmas:finance:update`, a resposta obrigatória + "Concluir apoio". Concluir
 * encerra o apoio; a revisão do supervisor só permanece nos casos legados
 * que ainda não tiveram o atendimento finalizado.
 * `onDone(dto)` recebe a resposta do backend (o caso).
 */
export const FinanceSupportBlock = ({ rmaId, support, canFinance = false, onDone = null }) => {
    const toast = useToast();
    const [answer, setAnswer] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!support || typeof support !== 'object') {
        return null;
    }
    const open = support.status === 'ABERTO';

    const conclude = async () => {
        if (!HasRequiredNote(answer)) {
            setError('Escreva a resposta do financeiro (pelo menos 3 caracteres).');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const result = await api.concludeFinanceSupport(rmaId, { note: answer.trim() });
            setAnswer('');
            toast.success('Apoio financeiro concluído; resposta registrada no caso.');
            await onDone?.(result);
        } catch (err) {
            toast.error(err?.message || 'Não foi possível concluir o apoio.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <ReviewEntry
                label="Pedido ao financeiro"
                name={support.requested_by_name}
                at={support.requested_at}
                text={support.request}
                tone="cyan"
            />
            {support.status === 'CONCLUIDO' || support.response ? (
                <ReviewEntry
                    label="Resposta do financeiro"
                    name={support.concluded_by_name}
                    at={support.concluded_at}
                    text={support.response}
                    tone="cyan"
                />
            ) : null}
            {open && canFinance ? (
                <div className="flex flex-col gap-1.5">
                    <label className="flex flex-col gap-1">
                        <span className={SECTION_LABEL}>Resposta do financeiro</span>
                        <textarea
                            value={answer}
                            rows={2}
                            maxLength={CASE_REVIEW_NOTE_MAX}
                            disabled={busy}
                            placeholder="O que foi verificado (obrigatório)"
                            aria-invalid={error ? true : undefined}
                            onChange={(e) => {
                                setAnswer(e.target.value);
                                if (error) {
                                    setError('');
                                }
                            }}
                            className={NOTE_FIELD_CLASSES}
                        />
                    </label>
                    <span className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={conclude}
                            disabled={busy || !answer.trim()}
                            className={PRIMARY_BUTTON_CLASSES}
                        >
                            {busy ? 'Concluindo…' : 'Concluir apoio'}
                        </button>
                        <span className={META_TEXT}>a resposta fica registrada no histórico do caso</span>
                    </span>
                    {error ? (
                        <span role="alert" className={ERROR_TEXT}>
                            {error}
                        </span>
                    ) : null}
                </div>
            ) : null}
            {open && !canFinance ? (
                <span className={META_TEXT}>aguardando o financeiro · responder exige rmas:finance:update</span>
            ) : null}
        </div>
    );
};

/** Atendimento finaliza uma vez; encaminhamento financeiro é uma escolha explícita. */
export const CaseReviewPanel = ({ occ, onChanged = null }) => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canFinish = checkPermission('rmas:mediation:update') || checkPermission('rmas:resolve');
    const canReopen = checkPermission('rmas:resolve');
    const canFinance = checkPermission('rmas:finance:update');
    const [local, setLocal] = useState(null);
    const [finishOpen, setFinishOpen] = useState(false);
    const [forwardToFinance, setForwardToFinance] = useState(false);
    const [note, setNote] = useState('');
    const [reopening, setReopening] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        setLocal(null);
    }, [occ?.review, occ?.finance_support]);
    const review = local?.review || occ?.review;
    const support = local ? local.finance_support : occ?.finance_support;
    if (!review) return null;
    const finished = Boolean(review.finished_at);
    const applyResult = (result) => {
        const dto = result?.rma || result;
        if (dto?.review) setLocal({ review: dto.review, finance_support: dto.finance_support ?? null });
    };
    const submit = async () => {
        setBusy(true);
        setError('');
        try {
            const result = reopening
                ? await api.reviewCase(occ.id, { decision: 'NAO_RESOLVIDO', note: note.trim() })
                : await api.finalizeCaseAttendance(occ.id, {
                      forward_to_finance: forwardToFinance,
                      note: note.trim() || null,
                  });
            applyResult(result);
            toast.success(
                reopening
                    ? 'Atendimento reaberto.'
                    : forwardToFinance
                      ? 'Atendimento finalizado e encaminhado ao financeiro.'
                      : 'Atendimento finalizado.',
            );
            setFinishOpen(false);
            setReopening(false);
            setNote('');
            setForwardToFinance(false);
            await onChanged?.();
        } catch (err) {
            setError(err.message || 'Não foi possível salvar o atendimento.');
        } finally {
            setBusy(false);
        }
    };
    return (
        <section
            aria-label="Atendimento do caso"
            className="flex flex-col gap-3 border-b border-mono-200 px-5 py-3 dark:border-mono-800"
        >
            <header className="flex flex-wrap items-center gap-2">
                <span className={SECTION_LABEL}>Atendimento</span>
                <CaseReviewBadge
                    state={{ label: finished ? 'Finalizado' : 'Em atendimento', tone: finished ? 'emerald' : 'sky' }}
                />
            </header>
            {finished ? (
                <ReviewEntry
                    label="Atendimento finalizado"
                    name={review.finished_by_name}
                    at={review.finished_at}
                    text={review.finished_note}
                    tone="emerald"
                />
            ) : review.note ? (
                <ReviewEntry
                    label={`Revisão: ${CASE_REVIEW_DECISION_LABELS[review.status] || review.status || 'Atendimento'}`}
                    name={review.decided_by_name}
                    at={review.decided_at}
                    text={review.note}
                    tone="sky"
                />
            ) : null}
            <FinanceSupportBlock
                rmaId={occ.id}
                support={support}
                canFinance={canFinance}
                onDone={async (result) => {
                    applyResult(result);
                    await onChanged?.();
                }}
            />
            {!finished && canFinish && !finishOpen ? (
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                        setFinishOpen(true);
                        setReopening(false);
                    }}
                    className={`${PRIMARY_BUTTON_CLASSES} self-start`}
                >
                    Finalizar caso
                </button>
            ) : null}
            {finished && canReopen && !finishOpen ? (
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                        setFinishOpen(true);
                        setReopening(true);
                    }}
                    className={`${SECONDARY_BUTTON_CLASSES} self-start`}
                >
                    Reabrir atendimento
                </button>
            ) : null}
            {finishOpen ? (
                <div className="flex flex-col gap-2 border border-mono-200 p-3 dark:border-mono-700">
                    <p className={META_TEXT}>
                        {reopening
                            ? 'Informe o que precisa ser retomado pelo atendimento.'
                            : 'Conclui o atendimento. Recebimento, inspeção e notas fiscais seguem com suas próprias pendências.'}
                    </p>
                    {!reopening ? (
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={forwardToFinance}
                                disabled={busy}
                                onChange={(event) => setForwardToFinance(event.target.checked)}
                            />
                            Encaminhar ao financeiro o que o atendimento não resolveu
                        </label>
                    ) : null}
                    {!reopening && support?.status === 'ABERTO' ? (
                        <p className={META_TEXT}>O apoio financeiro já solicitado continua aberto até a resposta.</p>
                    ) : null}
                    <textarea
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        rows={2}
                        maxLength={CASE_REVIEW_NOTE_MAX}
                        disabled={busy}
                        aria-label="Resumo do atendimento"
                        placeholder={
                            reopening
                                ? 'O que precisa ser retomado (obrigatório)'
                                : forwardToFinance
                                  ? 'O que não foi resolvido e o financeiro precisa verificar (obrigatório)'
                                  : 'Resumo da solução (opcional)'
                        }
                        className={NOTE_FIELD_CLASSES}
                    />
                    <span className="flex items-center gap-3">
                        <button
                            type="button"
                            disabled={busy || ((forwardToFinance || reopening) && !HasRequiredNote(note))}
                            onClick={submit}
                            className={PRIMARY_BUTTON_CLASSES}
                        >
                            {busy
                                ? 'Salvando…'
                                : reopening
                                  ? 'Confirmar reabertura'
                                  : forwardToFinance
                                    ? 'Finalizar e encaminhar'
                                    : 'Confirmar finalização'}
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                                setFinishOpen(false);
                                setReopening(false);
                                setNote('');
                                setForwardToFinance(false);
                                setError('');
                            }}
                            className={LINK_BUTTON_CLASS}
                        >
                            cancelar
                        </button>
                    </span>
                </div>
            ) : null}
            {error ? (
                <span role="alert" className={ERROR_TEXT}>
                    {error}
                </span>
            ) : null}
        </section>
    );
};

/**
 * "Apoio solicitado ao financeiro" na aba Financeiro: os casos do pedido com
 * `finance_support.status === 'ABERTO'`, cada um com o motivo do encaminhamento e
 * a mesma ação do painel do atendimento (resposta obrigatória + "Concluir
 * apoio"). O nº do caso abre a Pós-venda com ele expandido. Nenhum apoio em
 * aberto → não renderiza.
 */
export const FinanceSupportCasesPanel = ({
    ocorrencias = [],
    canFinance = false,
    onChanged = null,
    onOpenCase = null,
}) => {
    const rows = (Array.isArray(ocorrencias) ? ocorrencias : []).filter(
        (occ) => occ?.finance_support?.status === 'ABERTO',
    );
    if (!rows.length) {
        return null;
    }
    return (
        <Panel
            title="Apoio solicitado ao financeiro"
            action={
                <span className={META_TEXT}>
                    {canFinance ? 'motivo do encaminhamento no caso' : 'responder exige rmas:finance:update'}
                </span>
            }
        >
            <div className="flex flex-col divide-y divide-mono-100 dark:divide-mono-900">
                {rows.map((occ) => (
                    <div key={occ.id} className="flex flex-col gap-2 border-l-4 border-l-cyan-600 py-2.5 pl-3">
                        {onOpenCase ? (
                            <button
                                type="button"
                                onClick={() => onOpenCase(occ.id)}
                                title="Abrir o caso na Pós-venda"
                                className="self-start font-mono text-[12px] font-bold text-black underline-offset-2 hover:underline dark:text-white"
                            >
                                {OccurrenceTitle(occ)}
                            </button>
                        ) : (
                            <span className="font-mono text-[12px] font-bold text-black dark:text-white">
                                {OccurrenceTitle(occ)}
                            </span>
                        )}
                        <FinanceSupportBlock
                            rmaId={occ.id}
                            support={occ.finance_support}
                            canFinance={canFinance}
                            onDone={() => onChanged?.()}
                        />
                    </div>
                ))}
            </div>
        </Panel>
    );
};
